import { ArrowUpRight, Quote } from "lucide-react";
import { FadeIn, Eyebrow } from "./primitives";

const PLATFORM_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  other: "Elsewhere",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

const CardMeta = ({ post }) => (
  <div className="mt-6 flex items-center justify-between gap-3">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ruby">
      {post.kind === "link" ? PLATFORM_LABELS[post.platform] || "Post" : post.tag}
    </span>
    <time className="font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
      {formatDate(post.created_at)}
    </time>
  </div>
);

const cardClass =
  "flex w-80 shrink-0 flex-col justify-between border border-bone/10 bg-coal transition-colors duration-300 hover:border-ruby/40";

const FeedCard = ({ post }) => {
  if (post.kind === "tiktok" || post.kind === "youtube" || post.kind === "short") {
    return (
      <a
        href={post.link_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`feed-card-${post.id}`}
        className={`${cardClass} group overflow-hidden`}
      >
        {post.image_url && (
          <div className="relative">
            <img
              src={post.image_url}
              alt={post.title}
              loading="lazy"
              className="h-44 w-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100"
            />
            <span className="absolute left-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-bone backdrop-blur-sm">
              {post.kind === "short" ? "YouTube Short" : PLATFORM_LABELS[post.platform] || "Social"}
            </span>
          </div>
        )}
        <div className="flex flex-1 flex-col justify-between p-7">
          <h3 className="line-clamp-2 font-display text-lg font-medium leading-snug tracking-tight text-bone">
            {post.title}
          </h3>
          <div className="mt-6 flex items-center justify-between">
            <time className="font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
              {post.published_text || formatDate(post.created_at)}
            </time>
            <ArrowUpRight
              size={16}
              className="text-smoke transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ruby"
            />
          </div>
        </div>
      </a>
    );
  }

  if (post.kind === "quote") {
    return (
      <article data-testid={`feed-card-${post.id}`} className={`${cardClass} p-7`}>
        <div>
          <Quote size={18} className="text-ruby" />
          <p className="mt-5 font-display text-xl italic leading-snug text-bone">
            “{post.body}”
          </p>
        </div>
        <CardMeta post={post} />
      </article>
    );
  }

  if (post.kind === "link") {
    return (
      <a
        href={post.link_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`feed-card-${post.id}`}
        className={`${cardClass} group p-7`}
      >
        <div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ruby">
              {PLATFORM_LABELS[post.platform] || "Post"}
            </span>
            <ArrowUpRight
              size={16}
              className="text-smoke transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ruby"
            />
          </div>
          <h3 className="mt-5 font-display text-xl font-medium leading-snug tracking-tight text-bone">
            {post.title}
          </h3>
          <p className="mt-3 line-clamp-3 text-sm font-light leading-relaxed text-zinc-400">
            {post.body}
          </p>
        </div>
        <div className="mt-6">
          <time className="font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
            {formatDate(post.created_at)}
          </time>
        </div>
      </a>
    );
  }

  if (post.kind === "photo") {
    return (
      <article data-testid={`feed-card-${post.id}`} className={cardClass}>
        {post.image_url && (
          <img
            src={post.image_url}
            alt={post.title}
            loading="lazy"
            className="h-44 w-full object-cover opacity-90"
          />
        )}
        <div className="flex flex-1 flex-col justify-between p-7">
          <div>
            <h3 className="font-display text-xl font-medium leading-snug tracking-tight text-bone">
              {post.title}
            </h3>
            <p className="mt-3 line-clamp-3 text-sm font-light leading-relaxed text-zinc-400">
              {post.body}
            </p>
          </div>
          <CardMeta post={post} />
        </div>
      </article>
    );
  }

  return (
    <article data-testid={`feed-card-${post.id}`} className={`${cardClass} p-7`}>
      <div>
        <h3 className="font-display text-xl font-medium leading-snug tracking-tight text-bone">
          {post.title}
        </h3>
        <p className="mt-3 line-clamp-4 text-sm font-light leading-relaxed text-zinc-400">
          {post.body}
        </p>
      </div>
      <CardMeta post={post} />
    </article>
  );
};

export const Feed = ({ posts }) => {
  const loop = posts.length > 0 ? [...posts, ...posts] : [];

  return (
    <section id="feed" data-testid="feed-section" className="overflow-hidden border-t border-bone/10">
      <div className="mx-auto max-w-6xl px-6 pt-24">
        <FadeIn>
          <Eyebrow>The rolling feed</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-display text-3xl font-light tracking-tight text-bone sm:text-4xl">
            Fresh off the grid — clips, quotes and posts between episodes.
          </h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-zinc-400">
            Whatever lands on Instagram, TikTok or the salon notebook rolls through here.
            Hover to hold a card still.
          </p>
        </FadeIn>
      </div>

      <div className="marquee-mask mt-16 pb-24">
        {loop.length === 0 ? (
          <p className="px-6 font-mono text-xs uppercase tracking-[0.2em] text-smoke">
            The first post is being developed.
          </p>
        ) : (
          <div className="marquee-track flex w-max gap-5 px-6" data-testid="feed-track">
            {loop.map((post, index) => (
              <FeedCard key={`${post.id}-${index}`} post={post} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
